import { useSelector, useDispatch } from 'react-redux'
import { Container, Content, FlexboxGrid, Text } from 'rsuite'
import { selectCalculateTaxErrorMsg, selectCalculatedTaxInProgress, selectCalculatedTaxSuccess, selectTaxFlowData, selectTaxPaymentPeriods, selectTaxSteps } from '../../store/slices/tax/taxSelectors'
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import PortalHeader from '../../components/layout/PortalHeader'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import { resetCalculateTax, updateTaxFlowData } from '../../store/slices/tax/taxSlice'
import { calculateMultiPropertyTaxAsync } from '../../store/slices/tax/taxThunks'
import {
    selectConfigExpressTaxCollectionAgreementWith,
    selectConfigExpressTaxHasCollectionAgreement,
    selectConfigExpressTaxReportRevenueSubHeading,
    selectConfigExpressTaxReportRevenueText,
    selectConfigExpressTaxConfig
} from '../../store/slices/config/configSelectors'
import CollectionAgreementNotice from '../../components/tax/CollectionAgreementNotice'
import IconIndicator from '../../components/user-feedback/IconIndicator'
import Notice from '../../components/user-feedback/Notice'
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../common/enums'
import ReportRevenuePeriod from '../../components/tax/ReportRevenue/ReportRevenuePeriod'
import { setCurrentStep, setStepCompletedStatus } from '../../store/slices/flow/flowSlice'
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors'
import usePlaceNavigate from '../../hooks/usePlaceNavigate'
import { constructMarkup } from '../../common/utils'

const TaxReportRevenue = () => {
    const dispatch = useDispatch();
    const placeNavigate = usePlaceNavigate();

    const formData = useSelector(selectTaxFlowData);
    const selectedCurrentStep = useSelector(selectCurrentStep);
    const taxCalculationInProgress = useSelector(selectCalculatedTaxInProgress);
    const taxCalculationSucceeded = useSelector(selectCalculatedTaxSuccess);
    const taxCalculationErrorMessage = useSelector(selectCalculateTaxErrorMsg);
    const taxConfig = useSelector(selectConfigExpressTaxConfig);
    const reportRevenueSubHeading = useSelector(selectConfigExpressTaxReportRevenueSubHeading);
    const reportRevenueText = useSelector(selectConfigExpressTaxReportRevenueText);
    const hasCollectionAgreement = useSelector(selectConfigExpressTaxHasCollectionAgreement);
    const collectionAgreementWith = useSelector(selectConfigExpressTaxCollectionAgreementWith);
    const taxablePeriod = useSelector(selectTaxPaymentPeriods);

    // form refs for each license grouped by period
    // this is used so that we can validate the inputs of each ReportRevenueInput component on next click
    const [formRefs, setFormRefs] = useState({});

    const selectedLicenses = useMemo(() => {
        return formData.selectedLicenses || [];
    }, [formData?.selectedLicenses]);


    useEffect(() => {
        dispatch(resetCalculateTax());
    }, [dispatch])

    // Create a form ref for each license for each period
    useEffect(() => {
        const refs = selectedLicenses.reduce((acc, period) => {
            acc[period.startDate] = period.licenses.map(() => React.createRef());
            return acc;
        }, {});
        setFormRefs(refs);
    }, [selectedLicenses]);

    const validateAllInputs = async () => {
        const validationPromises = Object.values(formRefs).flat().map((formRef) => {
            return formRef.current.checkAsync();
        });

        const validationResults = await Promise.all(validationPromises);
        const allFormsValid = validationResults.every(result => !result.hasError);

        return allFormsValid;
    }

    const getUpdatedData = () => {
        return selectedLicenses.map(period => {
            const updatedPeriod = { ...period };
            updatedPeriod.licenses = updatedPeriod.licenses.map((license, index) => {
                const ref = formRefs[period.startDate][index];
                const lData = ref.current.getFormData();
                return { ...license, ...lData.license };
            });
            return updatedPeriod;
        });
    };

    // We have a use effect which has a dependency which checks if that has been completed and if it has will navigate to the next step
    const handleNextClick = useCallback(async () => {
        const allFormsValid = await validateAllInputs();

        if (allFormsValid) {
            dispatch(resetCalculateTax());
            // get updated data and save to redux
            const updatedSelectedLicenses = getUpdatedData();
            
            // wait for the calculations to run before continuing
            const calculationSuccessful = await dispatch(calculateMultiPropertyTaxAsync(updatedSelectedLicenses, hasCollectionAgreement, collectionAgreementWith, taxConfig));
            dispatch(updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses }));

            if (calculationSuccessful) {
                dispatch(setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true }));
                dispatch(setCurrentStep(selectedCurrentStep + 1));
                placeNavigate('/tax-confirm-and-sign');
            }
        }
    }, [validateAllInputs, dispatch, hasCollectionAgreement, collectionAgreementWith, taxConfig]);

    const handleBackClick = () => {
        dispatch(resetCalculateTax());
        const updatedSelectedLicenses = getUpdatedData();
        dispatch(updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses }));
        return true;
    }

    const taxablePeriodsText = useMemo(() => {
        if (reportRevenueSubHeading) {
            return reportRevenueSubHeading;
        }
        else {
            const periodString = taxablePeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ? "quarters" : "months";
            return `For the current and previous ${periodString}, please report the taxable receipts.`
        }
    }, [taxablePeriod, reportRevenueSubHeading]);

    return (
        <FlexboxGrid justify='space-between'>
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.PAY_TOT} />
            </FlexboxGrid.Item>

            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title='Report Revenue'
                        currentStep={2}
                        onNext={() => { handleNextClick(); }}
                        onBack={handleBackClick}
                        flowProgressStepsSelector={selectTaxSteps}
                    >
                        <Text className='text-large'>{taxablePeriodsText}</Text>
                        <Text className="paragraph text-small mb-24">{constructMarkup(reportRevenueText)}</Text>
                        {
                            hasCollectionAgreement && (
                                <CollectionAgreementNotice />
                            )
                        }
                        <div className='mt-24'>
                            {
                                Object.keys(formRefs).length > 0 && selectedLicenses?.map((period) => {
                                    return (
                                        <ReportRevenuePeriod
                                            key={`revenue_period_${period.startDate}`}
                                            period={period}
                                            periodRefs={formRefs[period.startDate]}
                                        />
                                    )
                                })
                            }
                        </div>
                        {
                            !taxCalculationSucceeded && taxCalculationErrorMessage && (
                                <Notice message={taxCalculationErrorMessage} />
                            )
                        }
                        {
                            taxCalculationInProgress && (
                                <IconIndicator />
                            )
                        }
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default TaxReportRevenue
